import { TERMINAL_ORDER_STATUSES } from '../constants/statusStyles';

export const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Last 7 Days' },
  { id: 'month', label: 'Last 30 Days' },
  { id: 'year', label: 'This Year' },
  { id: 'custom', label: 'Custom Range' },
];

export const getDateRange = (preset, customStart = '', customEnd = '') => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();

  switch (preset) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'week':
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start.setDate(end.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'year':
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      if (customStart && customEnd) {
        return {
          start: new Date(`${customStart}T00:00:00`),
          end: new Date(`${customEnd}T23:59:59`),
        };
      }
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
  }

  return { start, end };
};

export const getPreviousRange = ({ start, end }) => {
  const duration = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - duration);
  return { start: prevStart, end: prevEnd };
};

export const isInRange = (dateStr, range) => {
  if (!dateStr || !range) return false;
  const d = new Date(dateStr);
  return d >= range.start && d <= range.end;
};

export const growthIndicator = (current, previous) => {
  if (previous === 0 && current === 0) return { text: '0%', positive: true };
  if (previous === 0) return { text: '+100%', positive: true };
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.abs(pct).toFixed(1);
  return {
    text: `${pct >= 0 ? '+' : '-'}${rounded}%`,
    positive: pct >= 0,
  };
};

export const categorizeServiceRevenue = (orders) => {
  const buckets = {
    'Normal Ironing': 0,
    'Wash & Iron': 0,
    'Express Ironing': 0,
    'Express Wash & Iron': 0,
  };

  orders.forEach((o) => {
    const s = (o.serviceType || o.service || '').toLowerCase().trim();
    const amount = Number(o.totalAmount || o.amount || 0);

    if (s.includes('express') && (s.includes('iron') || s.includes('press')) && (s.includes('wash') || s.includes('fold'))) {
      buckets['Express Wash & Iron'] += amount;
    } else if (s.includes('express') && (s.includes('wash') || s.includes('fold'))) {
      buckets['Express Wash & Iron'] += amount;
    } else if (s.includes('express') && (s.includes('iron') || s.includes('press'))) {
      buckets['Express Ironing'] += amount;
    } else if (s.includes('wash') || s.includes('fold') || s.includes('normal wash')) {
      buckets['Wash & Iron'] += amount;
    } else if (s.includes('iron') || s.includes('press')) {
      buckets['Normal Ironing'] += amount;
    } else {
      buckets['Wash & Iron'] += amount;
    }
  });

  return buckets;
};

export const buildTopCustomers = (orders) => {
  const map = {};
  orders.forEach((o) => {
    const key = o.customerName || 'Unknown';
    if (!map[key]) {
      map[key] = {
        customerName: key,
        totalOrders: 0,
        totalRevenue: 0,
        lastOrderDate: o.date,
      };
    }
    map[key].totalOrders += 1;
    map[key].totalRevenue += o.totalAmount || o.amount || 0;
    if (new Date(o.date) > new Date(map[key].lastOrderDate)) {
      map[key].lastOrderDate = o.date;
    }
  });

  return Object.values(map).sort((a, b) => b.totalRevenue - a.totalRevenue);
};

export const buildStaffPerformance = (staff, orders) => {
  return staff.map((s) => {
    const handled = orders.filter(
      (o) => o.createdBy === s.name || o.createdBy === s.username
    ).length;
    const revenue = handled * 45 + (s.paymentsCollected || 0) * 0.1;
    return {
      id: s.id,
      staffName: s.name,
      role: s.role,
      ordersHandled: s.ordersHandled || handled,
      deliveriesCompleted: s.deliveriesCompleted || 0,
      revenueGenerated: Math.round(revenue * 100) / 100,
    };
  });
};

export const computeReportMetrics = ({
  orders,
  customers,
  payments,
  pickups,
  deliveries,
  staff,
  range,
}) => {
  const prevRange = getPreviousRange(range);
  const periodOrders = orders.filter((o) => isInRange(o.date, range));
  const prevOrders = orders.filter((o) => isInRange(o.date, prevRange));
  const periodPayments = payments.filter((p) => isInRange(p.date, range));

  const totalRevenue = periodOrders.reduce((s, o) => s + (o.totalAmount || o.amount || 0), 0);
  const prevRevenue = prevOrders.reduce((s, o) => s + (o.totalAmount || o.amount || 0), 0);

  const completedOrders = periodOrders.filter((o) => o.status === 'Delivered').length;
  const pendingOrders = periodOrders.filter(
    (o) => !TERMINAL_ORDER_STATUSES.includes(o.status) && o.status !== 'Delivered'
  ).length;
  const cancelledOrders = periodOrders.filter((o) => o.status === 'Return' || o.status === 'Cancelled').length;
  const deliveredOrders = completedOrders;

  const activeCustomers = customers.filter((c) => c.status === 'Active').length;
  const avgOrderValue = periodOrders.length ? totalRevenue / periodOrders.length : 0;

  const paidAmount = periodPayments.filter((p) => p.status === 'Paid').reduce((s, p) => s + p.amount, 0);
  const pendingAmount = periodPayments.filter((p) => p.status === 'Pending').reduce((s, p) => s + p.amount, 0);
  const partialAmount = periodPayments.filter((p) => p.status === 'Partial').reduce((s, p) => s + p.amount, 0);
  const dueCustomers = customers.filter((c) => (c.balance || 0) > 0).length;

  const periodPickups = pickups.filter((p) => isInRange(p.pickupDate, range));
  const completedPickups = periodPickups.filter((p) =>
    ['Completed', 'Picked Up'].includes(p.status)
  ).length;
  const periodDeliveries = deliveries.filter((d) => isInRange(d.deliveryDate, range));
  const failedDeliveries = periodDeliveries.filter((d) => d.status === 'Failed').length;
  const completedDeliveries = periodDeliveries.filter((d) => d.status === 'Delivered').length;

  const todayRange = getDateRange('today');
  const todayOrders = orders.filter((o) => isInRange(o.date, todayRange));
  const todayPayments = payments.filter((p) => isInRange(p.date, todayRange));

  const serviceRevenue = categorizeServiceRevenue(periodOrders);
  const topCustomers = buildTopCustomers(periodOrders);
  const staffPerformance = buildStaffPerformance(staff, periodOrders);

  const monthlyRevenue = totalRevenue;
  const prevMonthly = prevRevenue;

  return {
    summary: {
      totalRevenue,
      totalOrders: periodOrders.length,
      completedOrders,
      pendingOrders,
      activeCustomers,
      averageOrderValue: avgOrderValue,
      growth: {
        revenue: growthIndicator(totalRevenue, prevRevenue),
        orders: growthIndicator(periodOrders.length, prevOrders.length),
        completed: growthIndicator(completedOrders, prevOrders.filter((o) => o.status === 'Delivered').length),
        pending: growthIndicator(
          pendingOrders,
          prevOrders.filter((o) => !TERMINAL_ORDER_STATUSES.includes(o.status) && o.status !== 'Delivered').length
        ),
        customers: growthIndicator(activeCustomers, activeCustomers),
        avgOrder: growthIndicator(avgOrderValue, prevOrders.length ? prevRevenue / prevOrders.length : 0),
      },
    },
    daily: {
      revenueToday: todayOrders.reduce((s, o) => s + (o.totalAmount || o.amount || 0), 0),
      ordersToday: todayOrders.length,
      paymentsReceived: todayPayments.filter((p) => p.status === 'Paid').reduce((s, p) => s + p.amount, 0),
    },
    monthly: {
      monthlyRevenue,
      revenueGrowth: growthIndicator(monthlyRevenue, prevMonthly),
      revenueComparison: prevMonthly,
    },
    orders: {
      totalOrders: periodOrders.length,
      pendingOrders,
      deliveredOrders,
      cancelledOrders,
    },
    serviceRevenue,
    payments: { paidAmount, pendingAmount, partialAmount, dueCustomers },
    logistics: {
      totalPickups: periodPickups.length,
      completedPickups,
      totalDeliveries: periodDeliveries.length,
      failedDeliveries,
      completedDeliveries,
    },
    topCustomers,
    staffPerformance,
    periodOrders,
    paymentMethodBreakdown: periodPayments.reduce((acc, p) => {
      acc[p.method] = (acc[p.method] || 0) + p.amount;
      return acc;
    }, {}),
    orderStatusBreakdown: periodOrders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {}),
  };
};

export const generateMockReportData = (
  stepCategory,
  stepReportType,
  stepParameter,
  { orders = [], customers = [], staff = [], drivers = [], payments = [], pickups = [], deliveries = [], expenses = [] } = {}
) => {
  switch (stepReportType) {
    case 'total_sales': {
      const dateMap = {};
      orders.forEach((o) => {
        const d = (o.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10);
        if (!dateMap[d]) dateMap[d] = { date: d, count: 0, subtotal: 0, discount: 0, tax: 0, total: 0 };
        const amt = Number(o.totalAmount || o.amount || 0);
        dateMap[d].count += 1;
        dateMap[d].subtotal += amt * 0.95;
        dateMap[d].discount += amt * 0.05;
        dateMap[d].tax += amt * 0.05;
        dateMap[d].total += amt;
      });
      return Object.values(dateMap);
    }
    case 'sales_detail': {
      return orders.flatMap((o, idx) => {
        const items = o.items && o.items.length > 0
          ? o.items
          : [{ name: o.serviceType || 'Standard Laundry', service: o.serviceType || 'Normal', quantity: 1, unitPrice: o.totalAmount || 15, totalPrice: o.totalAmount || 15 }];
        return items.map((it, itemIdx) => ({
          id: `${o.id || idx}-${itemIdx}`,
          orderNo: o.number || o.id,
          customerName: o.customerName || 'Walk-in Customer',
          date: o.date || new Date().toISOString().slice(0, 10),
          itemName: it.name || it.itemName || 'Garment',
          service: it.service || o.serviceType || 'Normal Wash',
          qty: it.quantity || it.qty || 1,
          price: it.unitPrice || it.price || 15,
          total: it.totalPrice || it.total || (it.quantity || 1) * (it.unitPrice || 15),
        }));
      });
    }
    case 'branch_sales': {
      const branchMap = {};
      orders.forEach((o) => {
        const b = o.branch || 'Ragheey';
        if (!branchMap[b]) branchMap[b] = { branch: b, count: 0, revenue: 0 };
        branchMap[b].count += 1;
        branchMap[b].revenue += Number(o.totalAmount || o.amount || 0);
      });
      return Object.values(branchMap).map((b) => ({
        ...b,
        avgValue: b.count ? b.revenue / b.count : 0,
      }));
    }
    case 'payment_methods': {
      const methodMap = {};
      payments.forEach((p) => {
        const m = p.method || 'Cash';
        if (!methodMap[m]) methodMap[m] = { method: m, count: 0, collected: 0 };
        methodMap[m].count += 1;
        methodMap[m].collected += Number(p.amount || 0);
      });
      return Object.values(methodMap);
    }
    case 'customer_list': {
      return customers.map((c) => ({
        customerId: c.id,
        name: c.name,
        phone: c.phone || 'N/A',
        registered: c.registeredAt || c.createdAt || '2026-01-15',
        discount: c.discount || 0,
        ordersCount: orders.filter((o) => o.customerId === c.id || o.customerName === c.name).length,
        balance: c.balance || c.pendingBalance || 0,
      }));
    }
    case 'top_customers': {
      const custMap = {};
      orders.forEach((o) => {
        const name = o.customerName || 'Walk-in Customer';
        if (!custMap[name]) custMap[name] = { name, ordersCount: 0, spent: 0, lastDate: o.date };
        custMap[name].ordersCount += 1;
        custMap[name].spent += Number(o.totalAmount || o.amount || 0);
        if (new Date(o.date) > new Date(custMap[name].lastDate)) custMap[name].lastDate = o.date;
      });
      return Object.values(custMap).sort((a, b) => b.spent - a.spent);
    }
    case 'customer_debts': {
      return customers
        .filter((c) => (c.balance || c.pendingBalance || 0) > 0)
        .map((c) => ({
          name: c.name,
          phone: c.phone || 'N/A',
          registered: c.registeredAt || '2026-01-15',
          balance: c.balance || c.pendingBalance || 0,
        }));
    }
    case 'completed_jobs': {
      const dJobs = deliveries
        .filter((d) => ['Delivered', 'Completed'].includes(d.status))
        .map((d) => ({
          reqId: d.id,
          type: 'Delivery',
          customer: d.customerName || d.customer,
          date: d.deliveryDate || d.date,
          driver: d.assignedStaff || 'Unassigned',
          status: d.status,
        }));
      const pJobs = pickups
        .filter((p) => ['Completed', 'Picked Up'].includes(p.status))
        .map((p) => ({
          reqId: p.id,
          type: 'Pickup',
          customer: p.customerName || p.customer,
          date: p.pickupDate || p.date,
          driver: p.assignedStaff || 'Unassigned',
          status: p.status,
        }));
      return [...dJobs, ...pJobs];
    }
    case 'pending_jobs': {
      const dJobs = deliveries
        .filter((d) => !['Delivered', 'Completed'].includes(d.status))
        .map((d) => ({
          reqId: d.id,
          type: 'Delivery',
          customer: d.customerName || d.customer,
          date: d.deliveryDate || d.date,
          driver: d.assignedStaff || 'Unassigned',
          status: d.status,
        }));
      const pJobs = pickups
        .filter((p) => !['Completed', 'Picked Up'].includes(p.status))
        .map((p) => ({
          reqId: p.id,
          type: 'Pickup',
          customer: p.customerName || p.customer,
          date: p.pickupDate || p.date,
          driver: p.assignedStaff || 'Unassigned',
          status: p.status,
        }));
      return [...dJobs, ...pJobs];
    }
    case 'user_sales': {
      return staff.map((s) => {
        const sOrders = orders.filter(
          (o) => o.createdBy === s.name || o.createdBy === s.username || s.name === 'Admin User' || s.name === 'Counter Staff'
        );
        const totalSales = sOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
        return {
          name: s.name,
          count: sOrders.length,
          sales: totalSales,
          cashCollected: totalSales * 0.6,
          knetCollected: totalSales * 0.3,
          bukeyCollected: totalSales * 0.05,
          creditPending: totalSales * 0.05,
          cashExpenses: 12.5,
          netCashInHand: totalSales * 0.6 - 12.5,
        };
      });
    }
    case 'area_sales': {
      const areaMap = {};
      customers.forEach((c) => {
        const a = c.areaName || 'Salmiya';
        if (!areaMap[a]) areaMap[a] = { area: a, customerCount: 0, subscriberCount: 0, orderCount: 0, totalSales: 0 };
        areaMap[a].customerCount += 1;
        if (c.isSubscriber || c.subscriber) areaMap[a].subscriberCount += 1;
      });
      orders.forEach((o) => {
        const c = customers.find((cust) => cust.id === o.customerId || cust.name === o.customerName);
        const a = c?.areaName || 'Salmiya';
        if (!areaMap[a]) areaMap[a] = { area: a, customerCount: 1, subscriberCount: 0, orderCount: 0, totalSales: 0 };
        areaMap[a].orderCount += 1;
        areaMap[a].totalSales += Number(o.totalAmount || 0);
      });
      return Object.values(areaMap);
    }
    case 'shift_settlement': {
      return [
        {
          shift: 'Morning Shift (10:00 AM - 10:00 PM)',
          staffName: 'Counter Staff',
          morningStaff: ['Counter Staff', 'Ahmed Al-Sabah'],
          eveningStaff: ['Fatima Zahra'],
          invoicesCount: 18,
          totalRevenue: 345.5,
          cashCollected: 180.0,
          knetCollected: 120.5,
          bukeyCollected: 25.0,
          creditCollected: 20.0,
          cashExpenses: 15.0,
          netCashInHand: 165.0,
          isTotalRow: false,
        },
        {
          shift: 'Evening Shift (10:00 PM - 10:00 AM)',
          staffName: 'Fatima Zahra',
          morningStaff: ['Counter Staff'],
          eveningStaff: ['Fatima Zahra'],
          invoicesCount: 12,
          totalRevenue: 210.0,
          cashCollected: 110.0,
          knetCollected: 80.0,
          bukeyCollected: 10.0,
          creditCollected: 10.0,
          cashExpenses: 5.0,
          netCashInHand: 105.0,
          isTotalRow: false,
        },
      ];
    }
    case 'driver_income': {
      return (drivers || []).map((d) => {
        const dDeliveries = deliveries.filter((del) => del.assignedStaff === d.driverName);
        const dPickups = pickups.filter((p) => p.assignedStaff === d.driverName);
        const completedCount =
          dDeliveries.filter((del) => del.status === 'Delivered').length +
          dPickups.filter((p) => p.status === 'Completed').length;
        const activeDelCount = dDeliveries.filter((del) => del.status !== 'Delivered').length;
        const activePickCount = dPickups.filter((p) => p.status !== 'Completed').length;
        const salesGen = completedCount * 25;
        return {
          name: d.driverName,
          areas: (d.assignedAreas || ['Salmiya', 'Hawally']).join(', '),
          status: d.status || 'Available',
          activePickups: activePickCount,
          activeDeliveries: activeDelCount,
          count: completedCount || 8,
          sales: salesGen || 180.0,
          invoiceComm: (completedCount || 8) * 0.5,
          incomeComm: (salesGen || 180.0) * 0.05,
          totalComm: (completedCount || 8) * 0.5 + (salesGen || 180.0) * 0.05,
        };
      });
    }
    case 'service_revenue': {
      const servMap = {};
      orders.forEach((o) => {
        const s = o.serviceType || 'Normal Wash';
        if (!servMap[s]) servMap[s] = { service: s, count: 0, revenue: 0 };
        servMap[s].count += 1;
        servMap[s].revenue += Number(o.totalAmount || 0);
      });
      return Object.values(servMap);
    }
    case 'garment_stats': {
      return [
        { name: 'Dishdasha', qty: 45, revenue: 67.5 },
        { name: 'Ghutra', qty: 32, revenue: 32.0 },
        { name: 'Shirt', qty: 28, revenue: 42.0 },
        { name: 'Trousers', qty: 24, revenue: 36.0 },
        { name: 'Abaya', qty: 15, revenue: 37.5 },
        { name: 'Suit 2-Piece', qty: 12, revenue: 48.0 },
      ];
    }
    default:
      return [];
  }
};
